import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { createBrowserHistory } from "history";
import { useNavigate } from "react-router-dom";
import {
  removeInsignificantZero,
  raiseANewRequest,
  getUpdatedOrderInfo,
} from "../../Utilities";
import { useExchangeFetch, useHardCode, useAPI } from "../../hooks";
import { useSelector, useDispatch } from "react-redux";
import {
  updatePaymentSettings,
  updateOtherSettings,
  setProductDetails,
  updateExchangeSettingsDetails,
} from "../../redux/slices";
import {
  paymentSettingTypes,
  productActionTypes,
  otherSettingsActionTypes,
  exchangeSettingsActionTypes,
} from "../../redux/utils";
import { ItemCard, Toast, RazorPay, CashFree } from "..";
import { RaiseNewRequestButton } from "../../Components";
import { useAppContext } from "../../contexts";
import { CURRENCY_SYMBOLS } from "../../Utilities/currency";
import { IoAlertCircleOutline } from "react-icons/io5";

export const ExchangeSubmitted = () => {
  const storeConfigState = useSelector((state) => state.storeSettings);
  const productDetailsState = useSelector((state) => state.productDetails);
  const selectedProductDetailsState = useSelector(
    (state) => state.selectedProductDetails
  );
  const exchangeSettingsState = useSelector((state) => state.exchangeSettings);
  const paymentSettingsState = useSelector((state) => state.paymentSettings);
  const history = createBrowserHistory();
  const navigate = useNavigate();
  const { itemToBeReturnedTags } = useExchangeFetch();
  const { SHOPIFY_SHOP_OBJECT, CURRENT_STORE_NAME, PURE_FEMALE_TRANSLATION } =
    useHardCode();
  const isPureFemale = SHOPIFY_SHOP_OBJECT === CURRENT_STORE_NAME.pure_female;
  const coupon_code_info = isPureFemale
    ? PURE_FEMALE_TRANSLATION.exchange_submitted.desc
    : storeConfigState?.themeConfig?.use_below_coupon;
  const isImmaculateVegan =
    SHOPIFY_SHOP_OBJECT === CURRENT_STORE_NAME.immaculate_vegan;
  const paymentPageRef = useRef();
  const {
    isInternalServerError,
    setIsInternalServerError,
    setIsPaymentCompleted,
    isLoadingPayment,
    setIsLoadingPayment,
    razorPayPaymentSuccess,
  } = useAppContext();
  const dispatch = useDispatch();
  const [paymentSuccessHeading, setPaymentSuccessHeading] = useState(
    storeConfigState?.themeConfig?.exchange_payment_page_left_title
  );
  const [paymentSuccessDesc, setPaymentSuccessDesc] = useState(
    storeConfigState?.themeConfig?.exchange_payment_page_left_content
  );
  const [showPaymentUnverified, setShowPaymentUnverified] = useState(false)
  const otherSettingsState = useSelector((state) => state.otherSettings);
  const refundSettingsState = useSelector((state) => state.refundSettings);
  const { REFUND_EXCHANGE_PAYMENT_STATUS_PAYMENT_ID_API } = useAPI();
  const isShowRAN =
    SHOPIFY_SHOP_OBJECT === CURRENT_STORE_NAME.steve_maiden
      ? ""
      : `${otherSettingsState.ran}`;

  const old_item_name = exchangeSettingsState.selectedProductName;
  const old_item_value =
    CURRENCY_SYMBOLS[exchangeSettingsState?.availableCreditCurrency] +
    removeInsignificantZero(parseFloat(exchangeSettingsState.availableCredit));

  const old_item_variants = itemToBeReturnedTags
    ?.substring(0, itemToBeReturnedTags?.length - 1)
    .replace(/,/g, "/");
  const new_item_name = exchangeSettingsState.newItemName;
  const new_item_value =
    CURRENCY_SYMBOLS[exchangeSettingsState.newItemPriceCurrency] +
    removeInsignificantZero(
      parseFloat(exchangeSettingsState.newItemPrice) *
        parseFloat(selectedProductDetailsState.selectedQuantity)
    );

  const new_item_color =
    exchangeSettingsState?.variantLabels?.[0] +
    (exchangeSettingsState?.newItemVariations?.tags.length > 0
      ? exchangeSettingsState?.newItemVariations?.tags?.[0]
      : "");
  const new_item_size =
    exchangeSettingsState?.variantLabels?.[1] +
    exchangeSettingsState?.newItemVariations?.tags?.[1];
  const new_item_other =
    exchangeSettingsState?.variantLabels?.[2] +
    exchangeSettingsState?.newItemVariations?.tags?.[2];
  let new_item_variants =
    (new_item_color ? new_item_color + "/ " : "") +
    (new_item_size ? new_item_size + "/ " : "") +
    (new_item_other ? new_item_other : "");
  new_item_variants = new_item_variants
    ?.replace(/\s+$/, "")
    ?.replace(/\/$/, "");
  const exchangeSubmittedDefaultDescription =
    SHOPIFY_SHOP_OBJECT === CURRENT_STORE_NAME.pure_female
      ? PURE_FEMALE_TRANSLATION.common_text.final_submission_p2
      : `${storeConfigState?.themeConfig?.your_exchange_request} ${isShowRAN} ${storeConfigState?.themeConfig?.has_been_submitted}`;
  const exchangeSubmittedFinalDescription =
    SHOPIFY_SHOP_OBJECT === CURRENT_STORE_NAME.pure_female
      ? PURE_FEMALE_TRANSLATION.common_text.final_submission
      : exchangeSubmittedDefaultDescription;
  /* browser back button handler */
  useEffect(() => {
    return history.listen(() => {
      if (history.action === "POP") {
        navigate("/");
        dispatch(
          updateExchangeSettingsDetails({
            key: exchangeSettingsActionTypes.UPDATE_NEW_ITEM_VARIANTS_SHOPIFY,
            value: null,
          })
        );
      }
    });
  }, [history, navigate]);

  useEffect(() => {
    if (
      paymentSettingsState.paymentRequired &&
      !(['razorpay','cashfree'].includes(refundSettingsState?.collectPaymentThrough))
    ) {
      const fetchPaymentInfo = async () => {
        try {
          const response = await axios.get(
            `${REFUND_EXCHANGE_PAYMENT_STATUS_PAYMENT_ID_API}${paymentSettingsState.paymentID}/`
          );

          if (response?.data?.payment_status === "completed") {
            dispatch(
              updatePaymentSettings({
                key: paymentSettingTypes.UPDATE_PAYMENT_REQUIRED,
                value: false,
              })
            );

            dispatch(
              updateOtherSettings({
                key: otherSettingsActionTypes.UPDATE_RAN,
                value: response.data.ran,
              })
            );

            setIsPaymentCompleted(true);
            setIsLoadingPayment(false);

            setPaymentSuccessDesc(
              storeConfigState?.themeConfig
                ?.exchange_submission_page_left_content
                ? storeConfigState?.themeConfig
                    ?.exchange_submission_page_left_content
                : exchangeSubmittedFinalDescription
            );
            setPaymentSuccessHeading(
              storeConfigState?.themeConfig?.exchange_submission_page_left_title
            );
            clearInterval(timer);
            paymentPageRef.current.close();
          }
        } catch (error) {
          if (error?.response?.status === 500) {
            setIsInternalServerError(true);
          }
        }
      };

      const timer = setInterval(() => {
        fetchPaymentInfo();
      }, 3000);
      return () => clearInterval(timer);
    }

    // Get updated product details
    const updatedOrderInfo = getUpdatedOrderInfo(
      productDetailsState,
      selectedProductDetailsState
    );

    // Update the product list on Redux
    dispatch(
      setProductDetails({
        key: productActionTypes.SET_ORDER_INFO,
        value: updatedOrderInfo,
      })
    );
  }, [paymentSettingsState.paymentRequired]);

  const paymentHandler = () => {
    setPaymentSuccessDesc(
      storeConfigState?.themeConfig?.waiting_for_the_payment_confirmation
    );
    setPaymentSuccessHeading(
      storeConfigState?.themeConfig?.exchange_payment_page_left_title
    );
    setIsLoadingPayment(true);
    if (paymentSettingsState.paymentRequired) {
      paymentPageRef.current = window.open(
        paymentSettingsState.paymentLink,
        "_blank"
      );
    }
  };

  const coupon_code = (
    <button
      class="coupon_code saara-d-none"
      onClick={
        () => navigator.clipboard.writeText(paymentSettingsState.couponCode)
        // navigator.clipboard.writeText("ECO1005-IMGWRJH2N9")
      }
    >
      {paymentSettingsState.couponCode}
      <img src="assets/svg/copy.svg" alt="copy" />
    </button>
  );

  const buttonWithLoader = (
    <>
      <button type="button" className="saara__btn pay-block-btn">
        <div
          className=" saara-spinner-border saara-spinner-border-sm"
          role="status"
        >
          <span className="saara-visually-hidden"></span>
        </div>
      </button>
      {coupon_code}
    </>
  );

  const payBlock = isLoadingPayment ? (
    buttonWithLoader
  ) : (
    <>
      {refundSettingsState?.collectPaymentThrough === 'razorpay' ? (
        <RazorPay
          api_key={
            refundSettingsState?.razorPayObject?.payment_gateway_info
              ?.additional_data?.api_key
          }
          amount={
            refundSettingsState?.razorPayObject?.payment_gateway_info
              ?.additional_data?.amount
          }
          payment_description={
            refundSettingsState?.razorPayObject?.payment_gateway_info
              ?.additional_data?.payment_description
          }
          image={
            refundSettingsState?.razorPayObject?.payment_gateway_info
              ?.additional_data.store_name
            }
          collectPaymentThrough={
            refundSettingsState?.collectPaymentThrough 
          }
          notes={
            refundSettingsState?.razorPayObject?.payment_gateway_info
              ?.additional_data?.notes
          }
          razorpay_order_id={
            refundSettingsState?.razorPayObject?.payment_gateway_info
              ?.razorpay_order_id
          }
          store_name={storeConfigState?.storeName}
          route_name="exchange"
          setShowPaymentUnverified={setShowPaymentUnverified}
        />
  ) : refundSettingsState?.collectPaymentThrough === 'cashfree' ? (
    <CashFree
        cashfree_reference_id={
          refundSettingsState?.razorPayObject?.cashfree_reference_id
        }
        cashfree_session_id={
          refundSettingsState?.razorPayObject?.cashfree_session_id
        }
        collectPaymentThrough={
          refundSettingsState?.collectPaymentThrough 
        }
        store_name={storeConfigState?.storeName}
        route_name="exchange"
    />
  ) : (
        <>
          {" "}
          <button
            type="button"
            className="saara__btn pay-block-btn saara_custom_btn"
            onClick={paymentHandler}
          >
            {
              storeConfigState?.themeConfig
                ?.exchange_payment_page_pay_now_button_label
            }
          </button>
          <div className="coupon_code_main saara-d-none">
            <div className="coupon_code_info">{coupon_code_info}</div>
            {coupon_code}
          </div>
        </>
      )}
    </>
  )

  return (
    <main className="exchange-submit-container">
      {isInternalServerError && <Toast type="error" />}
      <div className="exchange-submit-main">
        <div className="exchange-submit-left">
          <div className="p23__wrapper">
            <div className="exchange-submitted">
              <h1 className="p23__title saara-title">
                {paymentSettingsState.paymentRequired
                  ? storeConfigState?.themeConfig
                      ?.exchange_payment_page_left_title
                  : storeConfigState?.themeConfig
                      ?.exchange_submission_page_left_title}
              </h1>

              { showPaymentUnverified &&
                <div className="p23__desc info__payment-items">
                  <span>
                    <IoAlertCircleOutline />
                  </span>
                  <p>{storeConfigState?.themeConfig?.unverified_payment_warning  || "Your payment for a previous return/exchange request is still being processed. Please wait at least 30 minutes from your last request before submitting a new one."}</p>
                </div>
              }

              {isImmaculateVegan && (
                <>
                  <p className="p23__desc description">
                    Thank you for submitting your exchange. Your exchange
                    request ID is {otherSettingsState.ran}.
                  </p>
                  <p className="p23__desc description">
                    Please look out for an email in the next 1 working day with
                    next steps.
                  </p>

                  <p className="p23__desc description">
                    Please check your junk inbox if you do not receive the email
                    within this timeframe.
                  </p>
                </>
              )}
              {isImmaculateVegan || (
                <p className="p23__desc description">
                  {paymentSettingsState.paymentRequired
                    ? storeConfigState?.themeConfig
                        ?.exchange_payment_page_left_content
                    : storeConfigState?.themeConfig
                        ?.exchange_submission_page_left_content
                    ? storeConfigState?.themeConfig
                        ?.exchange_submission_page_left_content
                    : exchangeSubmittedFinalDescription}
                </p>
              )}
            </div>
          </div>
          <div className=" btns shop-more-btn">
            {razorPayPaymentSuccess || (
              <div className="shop-more-block">
                {paymentSettingsState.paymentRequired && payBlock}
              </div>
            )}
            {razorPayPaymentSuccess && (
              <div className="shop-more-block">
                <a
                  href={`${storeConfigState.baseURL}`}
                  className="saara_custom_btn saara__btn btn__back shop_more_btn back-page"
                >
                  {
                    storeConfigState?.themeConfig
                      ?.continue_shopping_button_label
                  }
                </a>
              </div>
            )}
            {!paymentSettingsState.paymentRequired && <RaiseNewRequestButton />}
          </div>
        </div>
        <div className="item_card_main">
          <ItemCard
            label={
              SHOPIFY_SHOP_OBJECT === CURRENT_STORE_NAME.pure_female
                ? PURE_FEMALE_TRANSLATION.exchange_text.old
                : storeConfigState?.themeConfig
                    ?.exchange_review_old_product_tag_label
            }
            imageSrc={selectedProductDetailsState.selectedProductImage}
            value={`${
              SHOPIFY_SHOP_OBJECT === CURRENT_STORE_NAME.pure_female
                ? PURE_FEMALE_TRANSLATION.exchange_text.exchange_value
                : storeConfigState?.themeConfig
                    ?.exchange_product_detail_right_card_title
            }: ${old_item_value}`}
            name={old_item_name}
            variants={old_item_variants}
            labelClass="old_item_label"
          />
          <ItemCard
            label={
              SHOPIFY_SHOP_OBJECT === CURRENT_STORE_NAME.pure_female
                ? PURE_FEMALE_TRANSLATION.exchange_text.new
                : storeConfigState?.themeConfig
                    ?.exchange_review_new_product_tag_label
            }
            imageSrc={
              selectedProductDetailsState.shopifyRedirection
                ? selectedProductDetailsState.shopifySelectedProductImage
                : exchangeSettingsState?.newItemVariations?.img_url ||
                  otherSettingsState.defaultImage
            }
            value={
              selectedProductDetailsState.shopifyRedirection
                ? `Price:${new_item_value}`
                : `${
                    SHOPIFY_SHOP_OBJECT === CURRENT_STORE_NAME.pure_female
                      ? PURE_FEMALE_TRANSLATION.exchange_text.exchange_value
                      : storeConfigState?.themeConfig
                          ?.exchange_review_item_value_label
                  }: ${new_item_value} `
            }
            name={new_item_name}
            itemQty={exchangeSettingsState.newItemQty}
            variants={
              selectedProductDetailsState.shopifyRedirection
                ? exchangeSettingsState.newItemVariantsShopify
                : new_item_variants
            }
            labelClass="new_item_label"
            redirectedFromShopify={
              selectedProductDetailsState.shopifyRedirection
            }
          />{" "}
        </div>
      </div>
    </main>
  );
};
